import { useEffect, useState } from 'react';

import useApplicantStore from '../../applicantStore/useApplicantStore';
import useDashboardStore from '../../dashboardStore/useDashboardStore';
import { FeatureFlag } from '../../featureStore/enums/FeatureFlag';
import { useFeatureFlag } from '../../featureStore/hooks/useFeatureFlag';
import useUserStore from '../../userStore/useUserStore';
import { getEmbeddedInsuranceVerificationLink } from '../functions/getEmbeddedInsuranceVerificationLink';
import { AsyncExternalLink } from '../types/AsyncExternalLink';
import { ExternalLink } from '../types/ExternalLink';
import { InternalLink } from '../types/InternalLink';
import { Verification } from '../types/Verification';
import { VerificationLinkItem } from '../types/VerificationLinkItem';

/**
 * This hook is meant to handle the logic for generating verification links based on the provided verifications.
 * It processes each verification, checking conditions and determining if the link should be external or internal.
 * The links are then returned in a format suitable for the VerificationLink component.
 */
export const useVerificationLinks = (
  verifications: Verification[],
): { links: VerificationLinkItem[] } => {
  const [links, setLinks] = useState<VerificationLinkItem[]>([]);

  const {
    computed: {
      vehicleEmbeddedInsuranceLeadId,
      vehicleApplicationLeadChannelCode,
      vehicleApplicationType,
    },
  } = useDashboardStore();
  const {
    computed: { userEmailAddress },
  } = useUserStore();
  const { currentResidenceState } = useApplicantStore();

  const embeddedInsuranceRedirectEnabled = useFeatureFlag(
    FeatureFlag.ENABLE_EMBEDDED_INSURANCE_VERIFICATION_REDIRECT,
    {
      applicationType: vehicleApplicationType,
      emailAddress: userEmailAddress,
      leadChannelCode: vehicleApplicationLeadChannelCode,
      residenceState: currentResidenceState,
    },
  );

  useEffect((): void => {
    setLinks(
      verifications.map(({ name, path, status }) => {
        // default to internal link if no external logic applies
        let link: InternalLink | ExternalLink | AsyncExternalLink = {
          kind: 'internal',
          to: path,
        };

        // determine external link logic based on the verification path and feature flag
        const isProofOfAutoInsurance = path.includes('/upload-insurance');
        if (
          isProofOfAutoInsurance &&
          embeddedInsuranceRedirectEnabled &&
          vehicleEmbeddedInsuranceLeadId
        ) {
          link = getEmbeddedInsuranceVerificationLink();
        }

        return {
          link,
          name,
          status,
        };
      }),
    );
  }, [embeddedInsuranceRedirectEnabled]);

  return { links };
};
